import { useMemo, useRef, useState } from 'react';
import { formatDateTime } from '../lib/format';
import { useIsNarrow } from '../lib/useMediaQuery';

export interface Point {
  ts: string;
  value: number;
}

interface Props {
  title: string;
  unit: string;
  points: Point[];
  height?: number;
  format?: (v: number) => string;
  thresholds?: { value: number; label: string }[];
  refetching?: boolean;
}

/**
 * The SVG scales to its container, so a wide viewBox on a phone shrinks the
 * tick text with it. A narrow viewBox keeps labels at a readable size.
 */
const GEOMETRY = {
  wide: { W: 720, PAD: { top: 12, right: 64, bottom: 26, left: 44 }, xTicks: [0, 0.25, 0.5, 0.75, 1], yTicks: 4 },
  narrow: { W: 340, PAD: { top: 10, right: 46, bottom: 24, left: 34 }, xTicks: [0, 0.5, 1], yTicks: 3 },
} as const;

function niceTicks(min: number, max: number, count: number): number[] {
  if (!Number.isFinite(min) || !Number.isFinite(max)) return [];
  if (min === max) {
    min -= 1;
    max += 1;
  }
  const span = max - min;
  const raw = span / count;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const norm = raw / mag;
  const step = (norm < 1.5 ? 1 : norm < 3 ? 2 : norm < 7 ? 5 : 10) * mag;
  const start = Math.floor(min / step) * step;
  const ticks: number[] = [];
  for (let v = start; v <= max + step * 0.001; v += step) ticks.push(Math.round(v * 1000) / 1000);
  return ticks;
}

/**
 * Single-series line chart: 2px line, 10% area wash, hairline grid, crosshair
 * tooltip, endpoint label and a table twin. One series, so no legend.
 */
export function TimeSeriesChart({ title, unit, points, height, format, thresholds = [], refetching }: Props) {
  const narrow = useIsNarrow();
  const { W, PAD, xTicks: X_TICK_FRACTIONS, yTicks: Y_TICK_COUNT } = narrow ? GEOMETRY.narrow : GEOMETRY.wide;
  const chartHeight = height ?? (narrow ? 170 : 200);
  const [hover, setHover] = useState<number | null>(null);
  const [showTable, setShowTable] = useState(false);
  const svgRef = useRef<SVGSVGElement>(null);
  const fmt = format ?? ((v: number) => `${Math.round(v * 10) / 10}${unit ? ` ${unit}` : ''}`);

  const model = useMemo(() => {
    if (points.length === 0) return null;
    const xs = points.map((p) => new Date(p.ts).getTime());
    const ys = points.map((p) => p.value);
    const x0 = Math.min(...xs);
    const x1 = Math.max(...xs);
    let yMin = Math.min(...ys, ...thresholds.map((t) => t.value));
    let yMax = Math.max(...ys, ...thresholds.map((t) => t.value));
    const padY = (yMax - yMin || 1) * 0.15;
    yMin -= padY;
    yMax += padY;
    const ticks = niceTicks(yMin, yMax, Y_TICK_COUNT);
    if (ticks.length) {
      yMin = Math.min(yMin, ticks[0]);
      yMax = Math.max(yMax, ticks[ticks.length - 1]);
    }
    const plotW = W - PAD.left - PAD.right;
    const plotH = chartHeight - PAD.top - PAD.bottom;
    const sx = (t: number) => PAD.left + (x1 === x0 ? plotW / 2 : ((t - x0) / (x1 - x0)) * plotW);
    const sy = (v: number) => PAD.top + (1 - (v - yMin) / (yMax - yMin)) * plotH;
    const path = xs.map((x, i) => `${i === 0 ? 'M' : 'L'}${sx(x).toFixed(1)},${sy(ys[i]).toFixed(1)}`).join(' ');
    const area = `${path} L${sx(xs[xs.length - 1]).toFixed(1)},${(PAD.top + plotH).toFixed(1)} L${sx(xs[0]).toFixed(1)},${(PAD.top + plotH).toFixed(1)} Z`;
    const xTicks = X_TICK_FRACTIONS.map((f) => x0 + (x1 - x0) * f);
    const spanHours = (x1 - x0) / 3_600_000;
    const fmtX = (t: number) =>
      spanHours > 48
        ? new Date(t).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
        : new Date(t).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
    return { xs, ys, sx, sy, path, area, ticks, xTicks, fmtX, plotH, plotW, x0, x1 };
  }, [points, thresholds, chartHeight, W, PAD, X_TICK_FRACTIONS, Y_TICK_COUNT]);

  if (!model) {
    return (
      <div className="chart">
        <div className="chart__head">
          <span className="chart__title">{title}</span>
        </div>
        <div className="chart__empty">No readings in this range</div>
      </div>
    );
  }

  const onMove = (e: React.PointerEvent<SVGSVGElement>) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = ((e.clientX - rect.left) / rect.width) * W;
    const t = model.x0 + ((x - PAD.left) / model.plotW) * (model.x1 - model.x0);
    let best = 0;
    let bestD = Infinity;
    model.xs.forEach((xv, i) => {
      const d = Math.abs(xv - t);
      if (d < bestD) {
        bestD = d;
        best = i;
      }
    });
    setHover(best);
  };

  const last = points[points.length - 1];
  const hp = hover !== null ? points[hover] : null;
  const hx = hover !== null ? model.sx(model.xs[hover]) : 0;
  const hy = hover !== null ? model.sy(model.ys[hover]) : 0;

  return (
    <div className={`chart${refetching ? ' refetching' : ''}`}>
      <div className="chart__head">
        <span className="chart__title">{title}</span>
        <span className="chart__latest">
          Latest <b>{fmt(last.value)}</b> · {points.length} samples
        </span>
      </div>
      <div className="chart__wrap">
        <svg
          ref={svgRef}
          className="chart__svg"
          viewBox={`0 0 ${W} ${chartHeight}`}
          role="img"
          aria-label={`${title}, ${points.length} samples, latest ${fmt(last.value)}`}
          onPointerMove={onMove}
          onPointerLeave={() => setHover(null)}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'ArrowLeft') setHover((h) => Math.max(0, (h ?? points.length - 1) - 1));
            if (e.key === 'ArrowRight') setHover((h) => Math.min(points.length - 1, (h ?? -1) + 1));
          }}
        >
          {model.ticks.map((t) => (
            <g key={t}>
              <line className="chart__grid" x1={PAD.left} x2={W - PAD.right} y1={model.sy(t)} y2={model.sy(t)} />
              <text className="chart__tick" x={PAD.left - 8} y={model.sy(t) + 4} textAnchor="end">
                {t.toLocaleString()}
              </text>
            </g>
          ))}
          <line className="chart__axis" x1={PAD.left} x2={W - PAD.right} y1={PAD.top + model.plotH} y2={PAD.top + model.plotH} />
          {model.xTicks.map((t, i) => (
            <text key={i} className="chart__tick" x={model.sx(t)} y={chartHeight - 8} textAnchor={i === 0 ? 'start' : i === model.xTicks.length - 1 ? 'end' : 'middle'}>
              {model.fmtX(t)}
            </text>
          ))}
          {thresholds.map((th) => (
            <g key={th.label}>
              <title>{th.label}</title>
              <line className="chart__threshold" x1={PAD.left} x2={W - PAD.right} y1={model.sy(th.value)} y2={model.sy(th.value)} />
              <text className="chart__threshold-label" x={W - PAD.right + 6} y={model.sy(th.value) + 3}>
                {fmt(th.value)}
              </text>
            </g>
          ))}
          <path className="chart__area" d={model.area} />
          <path className="chart__line" d={model.path} />
          <circle className="chart__marker" r={4} cx={model.sx(model.xs[model.xs.length - 1])} cy={model.sy(last.value)} />
          <text className="chart__endlabel" x={model.sx(model.xs[model.xs.length - 1]) + 8} y={model.sy(last.value) + 4}>
            {fmt(last.value)}
          </text>
          {hp ? (
            <g>
              <line className="chart__crosshair" x1={hx} x2={hx} y1={PAD.top} y2={PAD.top + model.plotH} />
              <circle className="chart__marker" r={5} cx={hx} cy={hy} />
            </g>
          ) : null}
        </svg>
        {hp ? (
          <div
            className="chart__tooltip"
            style={{ left: `${(hx / W) * 100}%`, top: 0, transform: hx > W * 0.7 ? 'translateX(-110%)' : 'translateX(12px)' }}
          >
            <b>{fmt(hp.value)}</b>
            <div className="muted">{formatDateTime(hp.ts)}</div>
          </div>
        ) : null}
      </div>
      <div className="chart__footer">
        <span className="small muted">
          {formatDateTime(points[0].ts)} – {formatDateTime(last.ts)}
        </span>
        <button type="button" className="btn btn--ghost btn--sm" onClick={() => setShowTable((s) => !s)}>
          {showTable ? 'Hide table' : 'Show table'}
        </button>
      </div>
      {showTable ? (
        <div className="chart__table">
          <table className="table table--dense">
            <thead>
              <tr>
                <th>Time</th>
                <th className="num">{title}</th>
              </tr>
            </thead>
            <tbody>
              {[...points].reverse().map((p) => (
                <tr key={p.ts}>
                  <td>{formatDateTime(p.ts)}</td>
                  <td className="num">{fmt(p.value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
